import { TutorialLayout, CodePreview } from '../../../../shared/components';
import { tutorialLevels } from '../../../../shared/config/tutorials';
import { Alert, AlertDescription } from '@voilajsx/uikit/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@voilajsx/uikit/card';
import { Info, FolderTree } from 'lucide-react';

const SPATemplate = () => {
  return (
    <TutorialLayout levels={tutorialLevels}>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold mb-4">SPA Template</h1>
          <p className="text-lg text-muted-foreground">
            Single Page Application template with basic routing using React Router.
          </p>
        </div>

        <Alert>
          <Info className="w-4 h-4" />
          <AlertDescription>
            Best for: Small to medium applications with a few pages and simple navigation needs.
          </AlertDescription>
        </Alert>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Create a Project</h2>

          <CodePreview
            title="Scaffold SPA Template"
            code={`npx @voilajsx/uikit create my-app --spa

cd my-app
npm run dev`}
            language="bash"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Project Structure</h2>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FolderTree className="w-5 h-5" />
                Directory Layout
              </CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="text-sm font-mono bg-muted p-4 rounded-lg overflow-x-auto">
{`my-app/
├── public/
├── src/
│   ├── components/         # Shared components
│   │   └── Layout.tsx
│   ├── pages/              # Route pages
│   │   ├── Home.tsx
│   │   ├── About.tsx
│   │   └── Contact.tsx
│   ├── App.tsx            # Router setup
│   ├── main.tsx           # Entry point
│   └── index.css
├── index.html
├── package.json
├── tsconfig.json
└── vite.config.ts`}
              </pre>
            </CardContent>
          </Card>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Router Setup</h2>

          <CodePreview
            title="src/App.tsx"
            code={`import { BrowserRouter, Routes, Route } from 'react-router-dom';
import Layout from './components/Layout';
import Home from './pages/Home';
import About from './pages/About';
import Contact from './pages/Contact';

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route element={<Layout />}>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;`}
            language="tsx"
          />

          <CodePreview
            title="src/components/Layout.tsx"
            code={`import { Outlet, Link } from 'react-router-dom';
import { PageLayout } from '@voilajsx/uikit/page-layout';
import { Button } from '@voilajsx/uikit/button';

function Layout() {
  return (
    <PageLayout
      header={
        <header className="border-b border-border">
          <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
            <Link to="/" className="font-bold text-xl">My App</Link>
            <nav className="flex items-center gap-4">
              <Link to="/" className="text-muted-foreground hover:text-foreground">
                Home
              </Link>
              <Link to="/about" className="text-muted-foreground hover:text-foreground">
                About
              </Link>
              <Link to="/contact">
                <Button size="sm">Contact</Button>
              </Link>
            </nav>
          </div>
        </header>
      }
      footer={
        <footer className="border-t border-border py-8 text-center text-sm text-muted-foreground">
          © 2025 My App
        </footer>
      }
    >
      <Outlet />
    </PageLayout>
  );
}

export default Layout;`}
            language="tsx"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Page Components</h2>

          <CodePreview
            title="src/pages/Home.tsx"
            code={`function Home() {
  return (
    <div className="py-12">
      <div className="max-w-4xl mx-auto px-4">
        <h1 className="text-4xl font-bold mb-4">Welcome Home</h1>
        <p className="text-muted-foreground">
          This is the home page of your SPA.
        </p>
      </div>
    </div>
  );
}

export default Home;`}
            language="tsx"
          />

          <CodePreview
            title="src/pages/About.tsx"
            code={`function About() {
  return (
    <div className="py-12">
      <div className="max-w-4xl mx-auto px-4">
        <h1 className="text-4xl font-bold mb-4">About Us</h1>
        <p className="text-muted-foreground">
          Learn more about our company.
        </p>
      </div>
    </div>
  );
}

export default About;`}
            language="tsx"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Adding More Routes</h2>

          <CodePreview
            title="Nested Routes"
            code={`<Routes>
  <Route element={<Layout />}>
    <Route path="/" element={<Home />} />
    <Route path="/about" element={<About />} />

    {/* Nested routes */}
    <Route path="/blog">
      <Route index element={<BlogList />} />
      <Route path=":slug" element={<BlogPost />} />
    </Route>

    {/* Protected routes */}
    <Route element={<RequireAuth />}>
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/settings" element={<Settings />} />
    </Route>

    {/* Catch-all */}
    <Route path="*" element={<NotFound />} />
  </Route>
</Routes>`}
            language="tsx"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">When to Use SPA Template</h2>

          <div className="grid md:grid-cols-2 gap-4">
            <Card className="border-green-500/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-green-600">Good For</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Small websites (3-10 pages)</li>
                  <li>• Marketing sites with about/contact</li>
                  <li>• Simple blogs</li>
                  <li>• Portfolio with projects section</li>
                  <li>• Quick prototypes</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-red-500/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-red-600">Consider Alternatives</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Complex nested routes → Multi template</li>
                  <li>• Feature modules → FBCA template</li>
                  <li>• Dashboard apps → FBCA template</li>
                  <li>• Large codebases → FBCA template</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">SPA vs Single Template</h2>

          <div className="bg-muted/50 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-3 font-semibold">Feature</th>
                  <th className="text-left p-3 font-semibold">Single</th>
                  <th className="text-left p-3 font-semibold">SPA</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border">
                  <td className="p-3">Routing</td>
                  <td className="p-3 text-muted-foreground">None</td>
                  <td className="p-3 text-muted-foreground">React Router</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3">Pages</td>
                  <td className="p-3 text-muted-foreground">1</td>
                  <td className="p-3 text-muted-foreground">Multiple</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3">URL Structure</td>
                  <td className="p-3 text-muted-foreground">/</td>
                  <td className="p-3 text-muted-foreground">/about, /contact, etc.</td>
                </tr>
                <tr>
                  <td className="p-3">Complexity</td>
                  <td className="p-3 text-muted-foreground">Minimal</td>
                  <td className="p-3 text-muted-foreground">Low</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </TutorialLayout>
  );
};

export default SPATemplate;
